import { useState, useCallback, useEffect } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import { Close as CloseIcon, CloudUpload as CloudUploadIcon, Delete as DeleteIcon, Check as CheckIcon } from '@mui/icons-material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import JSZip from 'jszip';
import React from 'react';
import { userTimezoneHeaders } from '../../utils/userTimezoneHeader';

/** Max ZIP size in bytes (50MB). Can be overridden by backend config later. */
const ZIP_MAX_BYTES = 50 * 1024 * 1024;

const DESCRIPTION_MAX_LENGTH = 500;
const ALLOWED_ZONE_TYPES = ['Polygon', 'Line', 'Rectangle'];
const ACCURACY_MIN = 0;
const ACCURACY_MAX = 100;

const USE_CASES_API_BASE = process.env.REACT_APP_API_URL_FOR_AI_DETECTION || '';

/** Expected JSON config schema from ZIP. */
interface UseCaseConfigJson {
  use_case_name: string;
  use_case_type: string;
  description: string;
  zone_type: string;
  accuracy: number;
  image?: string;
}

const REQUIRED_JSON_KEYS: (keyof UseCaseConfigJson)[] = [
  'use_case_name', 'use_case_type', 'description', 'zone_type', 'accuracy'
];

function mapZoneType(zoneType: string): string {
  const z = (zoneType || '').toLowerCase();
  if (z === 'line') return 'Line';
  if (z === 'rectangle') return 'Rectangle';
  return 'Polygon';
}

function mapUseCaseType(useCaseType: string): string {
  const t = (useCaseType || '').toLowerCase().replace(/-/g, '_');
  if (t.includes('face')) return 'Face Recognition';
  if (t.includes('object')) return 'Object Detection';
  if (t.includes('crowd')) return 'Crowd Analysis';
  return useCaseType || '';
}

/** Allowed image extensions in ZIP; only these are accepted. */
const ALLOWED_IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png'];
const PREVIEW_IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.webp', '.gif'];
const PREFERRED_PREVIEW_NAMES = ['preview.jpg', 'preview.jpeg', 'preview.png', 'image.jpg', 'image.png'];

/** Result of successful ZIP parse: form data, .pt blob for backend, raw JSON, and optional preview image blob. */
type ZipParseSuccess = {
  success: true;
  formData: Record<string, string | number>;
  ptFileBlob: Blob;
  extractedJson: Record<string, unknown>;
  /** Preview image blob extracted from ZIP (e.g. preview.jpg) for local display. */
  imageBlob: Blob | null;
};

/** Validate and parse ZIP; extract config, one .pt and one .json; return .pt blob and raw JSON. */
async function validateAndParseZip(
  file: File,
  existingUseCaseNames: string[]
): Promise<ZipParseSuccess | { success: false; error: string }> {
  if (!file.name.toLowerCase().endsWith('.zip')) {
    return { success: false, error: 'File type must be .zip.' };
  }
  if (file.size > ZIP_MAX_BYTES) {
    return { success: false, error: `ZIP must not exceed ${ZIP_MAX_BYTES / (1024 * 1024)}MB.` };
  }

  let zip: JSZip;
  try {
    zip = await JSZip.loadAsync(file);
  } catch {
    return { success: false, error: 'Upload failed. The file may be corrupted or not a valid ZIP. Please try again.' };
  }

  const names = Object.keys(zip.files).filter((n) => !n.startsWith('__MACOSX') && !n.endsWith('/'));
  const ptFiles = names.filter((n) => n.toLowerCase().endsWith('.pt'));
  const jsonFiles = names.filter((n) => n.toLowerCase().endsWith('.json'));

  if (ptFiles.length !== 1) {
    return { success: false, error: 'ZIP must contain exactly one .pt file (model file).' };
  }
  if (jsonFiles.length !== 1) {
    return { success: false, error: 'ZIP must contain exactly one .json file (configuration file).' };
  }

  /** Require exactly one image; only .jpg, .jpeg, .png allowed. */
  const imageLikeExtensions = ['.jpg', '.jpeg', '.png', '.webp', '.gif', '.bmp', '.tiff', '.tif'];
  const imageFilesInZip = names.filter((n) => {
    const base = n.split('/').pop()?.toLowerCase() ?? n.toLowerCase();
    return imageLikeExtensions.some((ext) => base.endsWith(ext));
  });
  const allowedImageFiles = imageFilesInZip.filter((n) => {
    const base = n.split('/').pop()?.toLowerCase() ?? n.toLowerCase();
    return ALLOWED_IMAGE_EXTENSIONS.some((ext) => base.endsWith(ext));
  });
  if (allowedImageFiles.length === 0) {
    if (imageFilesInZip.length > 0) {
      return { success: false, error: 'ZIP contains image files that are not allowed. Only .jpg, .jpeg, and .png are allowed.' };
    }
    return { success: false, error: 'ZIP must contain exactly one image file (.jpg, .jpeg, or .png).' };
  }
  if (allowedImageFiles.length > 1) {
    return { success: false, error: 'ZIP must contain exactly one image file (.jpg, .jpeg, or .png). Multiple images are not allowed.' };
  }
  const disallowedImages = imageFilesInZip.filter((n) => !allowedImageFiles.includes(n));
  if (disallowedImages.length > 0) {
    return { success: false, error: 'ZIP contains image files that are not allowed. Only .jpg, .jpeg, and .png are allowed.' };
  }

  const jsonEntry = zip.files[jsonFiles[0]];
  if (!jsonEntry || jsonEntry.dir) {
    return { success: false, error: 'Upload failed. Could not read configuration file. Please try again.' };
  }

  const ptEntry = zip.files[ptFiles[0]];
  if (!ptEntry || ptEntry.dir) {
    return { success: false, error: 'Upload failed. Could not read model file. Please try again.' };
  }

  let raw: unknown;
  try {
    const text = await jsonEntry.async('string');
    raw = JSON.parse(text);
  } catch {
    return { success: false, error: 'Invalid configuration: .json file is not valid JSON.' };
  }

  if (!raw || typeof raw !== 'object') {
    return { success: false, error: 'Invalid configuration: .json must be an object.' };
  }

  const obj = raw as Record<string, unknown>;
  const missing = REQUIRED_JSON_KEYS.filter((k) => obj[k] === undefined || obj[k] === null || obj[k] === '');
  if (missing.length > 0) {
    return { success: false, error: `Invalid configuration: missing required fields: ${missing.join(', ')}.` };
  }

  const useCaseName = String(obj.use_case_name || '').trim();
  const modelFileName = ptFiles[0].split('/').pop() || file.name;
  if (useCaseName && existingUseCaseNames.some((n) => n.trim().toLowerCase() === useCaseName.toLowerCase())) {
    return { success: false, error: 'A use case with this name already exists.' };
  }

  let ptFileBlob: Blob;
  try {
    ptFileBlob = await ptEntry.async('blob');
  } catch {
    return { success: false, error: 'Upload failed. Could not read model file. Please try again.' };
  }

  const accuracyNum = Number(obj.accuracy);
  const accuracyPct = Number.isFinite(accuracyNum)
    ? (accuracyNum <= 1 ? Math.round(accuracyNum * 100) : Math.round(accuracyNum))
    : 0;

  /** Find and extract preview image from ZIP (e.g. preview.jpg) for local display. */
  let imageBlob: Blob | null = null;
  const imageFiles = names.filter((n) => {
    const lower = n.toLowerCase();
    const baseName = n.split('/').pop()?.toLowerCase() ?? lower;
    return PREVIEW_IMAGE_EXTENSIONS.some((ext) => baseName.endsWith(ext));
  });
  const configImagePath = typeof obj.image === 'string' ? String(obj.image).trim() : '';
  const preferredName = configImagePath ? configImagePath.replace(/^.*[/\\]/, '').toLowerCase() : null;
  const imageEntryName =
    imageFiles.find((n) => PREFERRED_PREVIEW_NAMES.some((p) => n.toLowerCase().endsWith(p))) ??
    (preferredName ? imageFiles.find((n) => n.toLowerCase().endsWith(preferredName)) : null) ??
    imageFiles[0];
  if (imageEntryName) {
    const entry = zip.files[imageEntryName];
    if (entry && !entry.dir) {
      try {
        imageBlob = await entry.async('blob');
      } catch {
        imageBlob = null;
      }
    }
  }

  const formData = {
    name: useCaseName || modelFileName.replace(/\.pt$/i, ''),
    useCaseType: mapUseCaseType(String(obj.use_case_type)),
    zoneType: mapZoneType(String(obj.zone_type)),
    accuracy: accuracyPct,
    description: String(obj.description || '').trim(),
    modelFile: modelFileName,
    imagePreview: typeof obj.image === 'string' ? obj.image : '',
  };

  return {
    success: true,
    formData,
    ptFileBlob,
    extractedJson: { ...obj },
    imageBlob,
  };
}

export interface AddUseCaseSavePayload {
  /** User-edited name. */
  name: string;
  /** User-edited description. */
  description: string;
  /** From JSON (read-only in form). */
  useCaseType: string;
  zoneType: string;
  accuracy: number;
  modelFile: string;
  imagePreview?: string;
  /** Full extracted JSON from the ZIP config file (for backend). */
  extractedJson: Record<string, unknown>;
  /** Extracted .pt model file from ZIP (for backend). */
  ptFile: Blob;
  /** True if use case was created via POST /use-cases/create on Save; parent should refetch list. */
  createdViaApi?: boolean;
}

interface AddUseCaseDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: AddUseCaseSavePayload) => void;
  /** Existing use case / model names for duplicate check. */
  existingModelNames?: string[];
  /** Existing use case names for uniqueness validation (Step 2). */
  existingUseCaseNames?: string[];
}

export function AddUseCaseDialog({ open, onClose, onSave, existingModelNames = [], existingUseCaseNames = [] }: AddUseCaseDialogProps) {
  const [currentStep, setCurrentStep] = useState<1 | 2>(1);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  /** Extracted .pt file from ZIP (for backend); not editable. */
  const [extractedPtFile, setExtractedPtFile] = useState<Blob | null>(null);
  /** Raw JSON from ZIP config (submitted with save). */
  const [extractedJson, setExtractedJson] = useState<Record<string, unknown> | null>(null);
  /** Preview image blob extracted from ZIP (e.g. preview.jpg) for local display in Step 2. */
  const [extractedImageBlob, setExtractedImageBlob] = useState<Blob | null>(null);
  /** True after we successfully created use case via POST /use-cases/create on Save. */
  const [createdViaApi, setCreatedViaApi] = useState(false);
  /** Response from POST /use-cases/upload-model; used in POST /use-cases/create on Save. */
  const [uploadedModelPath, setUploadedModelPath] = useState<string | null>(null);
  const [uploadedModelSize, setUploadedModelSize] = useState<number>(0);
  const [uploadedImagePath, setUploadedImagePath] = useState<string>('');
  const [isUploadingModel, setIsUploadingModel] = useState(false);
  const [uploadModelError, setUploadModelError] = useState<string | null>(null);
  /** True when the preview image from upload response failed to load (e.g. wrong URL or CORS). */
  const [imagePreviewLoadError, setImagePreviewLoadError] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    useCaseType: '',
    zoneType: '',
    accuracy: 0,
    description: '',
    modelFile: '',
    imagePreview: '',
  });

  /** Object URL for extracted image blob; used for preview so it works without backend serving the image. */
  const [extractedImageObjectUrl, setExtractedImageObjectUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!extractedImageBlob) {
      setExtractedImageObjectUrl(null);
      return;
    }
    const url = URL.createObjectURL(extractedImageBlob);
    setExtractedImageObjectUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [extractedImageBlob]);

  const handleClose = useCallback(() => {
    setCurrentStep(1);
    setUploadedFile(null);
    setExtractedPtFile(null);
    setExtractedJson(null);
    setExtractedImageBlob(null);
    setCreatedViaApi(false);
    setUploadedModelPath(null);
    setUploadedModelSize(0);
    setUploadedImagePath('');
    setCreateError(null);
    setUploadModelError(null);
    setImagePreviewLoadError(false);
    setValidationError(null);
    setUploadError(null);
    setFormData({
      name: '',
      useCaseType: '',
      zoneType: '',
      accuracy: 0,
      description: '',
      modelFile: '',
      imagePreview: '',
    });
    onClose();
  }, [onClose]);

  const handleRetry = useCallback(() => {
    setUploadedFile(null);
    setExtractedPtFile(null);
    setExtractedJson(null);
    setExtractedImageBlob(null);
    setUploadedModelPath(null);
    setUploadedModelSize(0);
    setUploadedImagePath('');
    setImagePreviewLoadError(false);
    setValidationError(null);
    setUploadError(null);
  }, []);

  /** Remove uploaded file and reset to empty state (Step 1). */
  const handleDeleteFile = useCallback(() => {
    setUploadedFile(null);
    setExtractedPtFile(null);
    setExtractedJson(null);
    setExtractedImageBlob(null);
    setUploadedModelPath(null);
    setUploadedModelSize(0);
    setUploadedImagePath('');
    setImagePreviewLoadError(false);
    setValidationError(null);
    setUploadError(null);
    setFormData({
      name: '',
      useCaseType: '',
      zoneType: '',
      accuracy: 0,
      description: '',
      modelFile: '',
      imagePreview: '',
    });
  }, []);

  /** Re-upload: open file input (same as clicking drop zone). */
  const handleReUpload = useCallback(() => {
    setValidationError(null);
    setUploadError(null);
    document.getElementById('file-upload-input')?.click();
  }, []);

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleFileSelection = useCallback(async (file: File) => {
    setValidationError(null);
    setUploadError(null);
    setIsProcessing(true);
    try {
      const result = await validateAndParseZip(file, existingUseCaseNames);
      if (result.success) {
        setUploadedFile(file);
        setExtractedPtFile(result.ptFileBlob);
        setExtractedJson(result.extractedJson);
        setExtractedImageBlob(result.imageBlob ?? null);
        setFormData({
          name: String(result.formData.name),
          useCaseType: String(result.formData.useCaseType),
          zoneType: String(result.formData.zoneType),
          accuracy: Number(result.formData.accuracy),
          description: String(result.formData.description),
          modelFile: String(result.formData.modelFile),
          imagePreview: String(result.formData.imagePreview || ''),
        });
      } else {
        setValidationError(result.error);
        setUploadedFile(null);
      }
    } catch {
      setUploadError('Upload failed. Please try again.');
      setUploadedFile(null);
    } finally {
      setIsProcessing(false);
    }
  }, [existingUseCaseNames]);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (!file.name.toLowerCase().endsWith('.zip')) {
        setValidationError('File type must be .zip.');
        return;
      }
      handleFileSelection(file);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelection(e.target.files[0]);
    }
    e.target.value = '';
  };

  const handleBack = () => {
    setCurrentStep(1);
  };

  /** POST /use-cases/upload-model with zip file; on success go to Step 2. */
  const handleNext = useCallback(async () => {
    if (!uploadedFile || !extractedPtFile || !extractedJson) return;

    const base = USE_CASES_API_BASE.trim();
    if (!base) {
      setCreateError('API base URL is not configured. Add REACT_APP_API_URL_FOR_AI_DETECTION to your .env file.');
      return;
    }

    setIsUploadingModel(true);
    setCreateError(null);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('zip_file', uploadedFile);
      const url = `${base.replace(/\/$/, '')}/use-cases/upload-model`;
      const res = await fetch(url, {
        method: 'POST',
        body: formDataUpload,
        headers: { ...userTimezoneHeaders() },
      });
      const contentType = res.headers.get('content-type');
      let modelPath: string | null = null;
      let imagePreviewUrl = '';
      if (contentType && contentType.includes('application/json')) {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          if (res.status === 422 && Array.isArray(data.detail)) {
            const messages = data.detail.map((d: { msg?: string }) => d.msg).filter(Boolean);
            setCreateError(messages.length ? messages.join(' ') : 'Validation failed.');
          } else {
            setCreateError(data.message || (typeof data.detail === 'string' ? data.detail : '') || res.statusText || `Upload failed (${res.status}).`);
          }
          return;
        }
        const payload = data?.data && typeof data.data === 'object' ? data.data : data;
        modelPath =
          typeof data === 'string'
            ? data
            : (payload?.model_path ?? payload?.path ?? data?.model_path ?? data?.path ?? formData.modelFile) ?? '';
        const imageUrlFromApi = payload?.image_url ?? payload?.preview_url ?? data?.image_url ?? data?.preview_url;
        const imagePath = payload?.image_path ?? data?.image_path;
        if (typeof imageUrlFromApi === 'string' && imageUrlFromApi.trim()) {
          imagePreviewUrl = imageUrlFromApi.trim();
        } else if (imagePath && typeof imagePath === 'string') {
          const path = imagePath.replace(/^\.?\//, '');
          if (path) {
            try {
              const imageOrigin = base ? new URL(base).origin : '';
              imagePreviewUrl = imageOrigin ? `${imageOrigin}/${path}` : `${base.replace(/\/$/, '')}/${path}`;
            } catch {
              imagePreviewUrl = `${base.replace(/\/$/, '')}/${path}`;
            }
          }
        }
        if (payload && typeof payload === 'object') {
          setImagePreviewLoadError(false);
          const modelSize = payload.model_size != null ? Number(payload.model_size) : 0;
          const imagePathStr = typeof payload.image_path === 'string' ? payload.image_path : '';
          setUploadedModelSize(Number.isFinite(modelSize) ? modelSize : 0);
          setUploadedImagePath(imagePathStr);
          setFormData((prev) => ({
            ...prev,
            ...(imagePreviewUrl ? { imagePreview: imagePreviewUrl } : {}),
            ...(payload.name != null && String(payload.name).trim() ? { name: String(payload.name).trim() } : {}),
            ...(payload.description != null ? { description: String(payload.description) } : {}),
            ...(payload.use_case_type != null ? { useCaseType: String(payload.use_case_type) } : {}),
            ...(payload.zone_type != null ? { zoneType: mapZoneType(String(payload.zone_type)) } : {}),
            ...(payload.accuracy != null ? { accuracy: Number(payload.accuracy) } : {}),
          }));
        }
      } else {
        const text = await res.text();
        if (!res.ok) {
          setCreateError(text || res.statusText || `Upload failed (${res.status}).`);
          return;
        }
        modelPath = text || formData.modelFile;
      }
      setUploadedModelPath(modelPath);
      setCurrentStep(2);
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Failed to upload model.');
    } finally {
      setIsUploadingModel(false);
    }
  }, [uploadedFile, extractedPtFile, extractedJson, formData.modelFile]);


  /** Step 2 validations: returns error messages for name and description; other fields validated inline. */
  const getStep2ValidationErrors = useCallback((): { name: string | null; description: string | null; accuracy: string | null; zoneType: string | null } => {
    const nameTrimmed = formData.name.trim();
    if (!nameTrimmed) return { name: 'Use Case Name is required.', description: null, accuracy: null, zoneType: null };
    const nameExists = existingUseCaseNames.some((n) => n.trim().toLowerCase() === nameTrimmed.toLowerCase());
    if (nameExists) return { name: 'This name is already used by another use case.', description: null, accuracy: null, zoneType: null };

    const desc = formData.description.trim();
    if (!desc) return { name: null, description: 'Description is required.', accuracy: null, zoneType: null };
    if (desc.length > DESCRIPTION_MAX_LENGTH) return { name: null, description: `Description must be ${DESCRIPTION_MAX_LENGTH} characters or fewer.`, accuracy: null, zoneType: null };

    const acc = formData.accuracy;
    const validAccuracy = typeof acc === 'number' && !Number.isNaN(acc) && acc >= ACCURACY_MIN && acc <= ACCURACY_MAX;
    if (!validAccuracy) return { name: null, description: null, accuracy: `Accuracy must be between ${ACCURACY_MIN} and ${ACCURACY_MAX} (or 0–1).`, zoneType: null };

    const zoneTypeNorm = (formData.zoneType || '').trim();
    const zoneValid =
      zoneTypeNorm &&
      ALLOWED_ZONE_TYPES.some((z) => z.toLowerCase() === zoneTypeNorm.toLowerCase());
    if (!zoneValid) return { name: null, description: null, accuracy: null, zoneType: 'Zone type is not allowed.' };

    return { name: null, description: null, accuracy: null, zoneType: null };
  }, [formData.name, formData.description, formData.accuracy, formData.zoneType, existingUseCaseNames]);

  const step2Errors = getStep2ValidationErrors();
  const isStep2Valid =
    !step2Errors.name &&
    !step2Errors.description &&
    !step2Errors.accuracy &&
    !step2Errors.zoneType &&
    extractedPtFile != null &&
    extractedJson != null;

  /** POST /use-cases/create with payload from form and upload response (model_path, model_size, image_path). */
  const handleSave = useCallback(async () => {
    if (!extractedPtFile || !extractedJson || !isStep2Valid) return;
    const base = USE_CASES_API_BASE.trim();
    if (!base) {
      setCreateError('API base URL is not configured. Add REACT_APP_API_URL_FOR_AI_DETECTION to your .env file.');
      return;
    }
    const useCaseType = String((formData.useCaseType || extractedJson.use_case_type) ?? '').trim() || 'object_detection';
    const zoneType = (formData.zoneType || 'polygon').toLowerCase();
    const modelPath = uploadedModelPath ?? (extractedJson.model_path as string) ?? formData.modelFile ?? '';
    const body = {
      name: formData.name.trim(),
      description: formData.description.trim(),
      use_case_type: useCaseType,
      zone_type: zoneType,
      accuracy: Number(formData.accuracy) || 0,
      model_path: modelPath,
      model_size: uploadedModelSize,
      image_path: uploadedImagePath,
    };

    setIsCreating(true);
    setCreateError(null);
    setUploadModelError(null);
    try {
      const url = `${base.replace(/\/$/, '')}/use-cases/create`;
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...userTimezoneHeaders() },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        if (res.status === 422 && Array.isArray(data.detail)) {
          const messages = data.detail.map((d: { msg?: string }) => d.msg).filter(Boolean);
          setUploadModelError(messages.length ? messages.join(' ') : 'Validation failed.');
        } else {
          setUploadModelError(data.message || data.detail || res.statusText || `Request failed (${res.status}).`);
        }
        return;
      }
      setCreatedViaApi(true);
      onSave({
        name: formData.name.trim(),
        description: formData.description.trim(),
        useCaseType: formData.useCaseType,
        zoneType: formData.zoneType,
        accuracy: formData.accuracy,
        modelFile: formData.modelFile,
        imagePreview: formData.imagePreview || undefined,
        extractedJson,
        ptFile: extractedPtFile,
        createdViaApi: true,
      });
      handleClose();
    } catch (err) {
      setUploadModelError(err instanceof Error ? err.message : 'Failed to create use case.');
    } finally {
      setIsCreating(false);
    }
  }, [isStep2Valid, extractedPtFile, extractedJson, formData, uploadedModelPath, uploadedModelSize, uploadedImagePath, onSave]);

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') handleClose();
      }}
      disableEscapeKeyDown
      maxWidth={false}
      PaperProps={{
        sx: {
          width: 700,
          borderRadius: '10px',
          boxShadow: '0px 25px 50px 0px rgba(0,0,0,0.25)',
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          py: 2,
          borderBottom: '0.8px solid #E5E7EB',
        }}
      >
        <Typography
          sx={{
            fontFamily: 'Roboto, sans-serif',
            fontSize: '18px',
            fontWeight: 600,
            color: '#101828',
          }}
        >
          Add AI Detection Use Case
        </Typography>
        <IconButton
          size="small"
          onClick={handleClose}
          sx={{
            color: '#99A1AF',
            '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' },
          }}
        >
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>

      {/* Progress Indicator */}
      <Box sx={{ bgcolor: '#F9FAFB', px: 1.5, py: 0.75 }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Box
            sx={{
              flex: 1,
              height: 6,
              borderRadius: '26843500px',
              bgcolor: '#2932E5',
            }}
          />
          <Box
            sx={{
              flex: 1,
              height: 6,
              borderRadius: '26843500px',
              bgcolor: currentStep === 2 ? '#2932E5' : '#D1D5DC',
            }}
          />
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ p: currentStep === 1 ? 3 : 1.25 }}>
        {currentStep === 1 ? (
          // Step 1: ZIP Upload
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {(validationError || uploadError || createError) && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.5,
                  p: 1.5,
                  borderRadius: 1,
                  bgcolor: '#FEF2F2',
                  border: '1px solid #FECACA',
                }}
              >
                <ErrorOutlineIcon sx={{ color: '#B91C1C', fontSize: 22, flexShrink: 0, mt: 0.25 }} />
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: '#991B1B', fontWeight: 500 }}>
                    {validationError || uploadError || createError}
                  </Typography>
                  <Button
                    startIcon={<RefreshIcon />}
                    onClick={() => {
                      setCreateError(null);
                      if (validationError || uploadError) handleRetry();
                    }}
                    size="small"
                    sx={{
                      mt: 1,
                      textTransform: 'none',
                      color: '#2932E5',
                      '&:hover': { bgcolor: 'rgba(41, 50, 229, 0.08)' },
                    }}
                  >
                    Try again
                  </Button>
                </Box>
              </Box>
            )}

            {uploadedFile && !validationError && !uploadError ? (
              /* Uploaded file: success icon, file name, size, Delete File & Re-upload File buttons */
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  py: 4,
                  px: 2,
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    bgcolor: '#22C55E',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <CheckIcon sx={{ fontSize: 48, color: 'white' }} />
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography
                    sx={{
                      fontFamily: 'Roboto, sans-serif',
                      fontSize: '16px',
                      fontWeight: 700,
                      color: '#101828',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      maxWidth: 400,
                    }}
                  >
                    {uploadedFile.name}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: 'Roboto, sans-serif',
                      fontSize: '14px',
                      color: '#6B7280',
                      mt: 0.5,
                    }}
                  >
                    {(uploadedFile.size / (1024 * 1024)).toFixed(2)} MB
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5, mt: 1 }}>
                  <Button
                    variant="contained"
                    startIcon={<DeleteIcon sx={{ fontSize: 18 }} />}
                    onClick={handleDeleteFile}
                    sx={{
                      textTransform: 'none',
                      fontFamily: 'Roboto, sans-serif',
                      fontSize: '14px',
                      fontWeight: 500,
                      bgcolor: '#DC2626',
                      color: 'white',
                      '&:hover': { bgcolor: '#B91C1C' },
                    }}
                  >
                    Delete File
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<CloudUploadIcon sx={{ fontSize: 18 }} />}
                    onClick={handleReUpload}
                    sx={{
                      textTransform: 'none',
                      fontFamily: 'Roboto, sans-serif',
                      fontSize: '14px',
                      fontWeight: 500,
                      bgcolor: '#2932E5',
                      color: 'white',
                      '&:hover': { bgcolor: '#1f28b8' },
                    }}
                  >
                    Re-upload File
                  </Button>
                </Box>
              </Box>
            ) : (
              <Box
                sx={{
                  border: '1.6px dashed #CBD5E1',
                  borderRadius: '12px',
                  bgcolor: dragActive ? 'rgba(41, 50, 229, 0.05)' : 'transparent',
                  p: 6,
                  textAlign: 'center',
                  cursor: isProcessing ? 'wait' : 'pointer',
                  position: 'relative',
                  minHeight: 251,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  pointerEvents: isProcessing ? 'none' : 'auto',
                  opacity: isProcessing ? 0.7 : 1,
                }}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={() => !isProcessing && document.getElementById('file-upload-input')?.click()}
              >
                {isProcessing ? (
                  <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: '#64748B' }}>
                    Validating ZIP and reading configuration...
                  </Typography>
                ) : (
                  <>
                    <Box sx={{ mb: 2 }}>
                      <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
                        <path d="M23.9987 42.6667H39.9987V26.6667H50.6654L31.9987 8L13.332 26.6667H23.9987V42.6667ZM13.332 48H50.6654V53.3333H13.332V48Z" fill="#94A3B8" />
                      </svg>
                    </Box>
                    <Typography sx={{ mb: 1 }}>
                      <span style={{ fontFamily: 'Arial, sans-serif', fontSize: '16px', fontWeight: 700, color: '#2932E5' }}>Click to upload</span>
                      <span style={{ fontFamily: 'Arial, sans-serif', fontSize: '16px', color: '#212121' }}> or drag and drop</span>
                    </Typography>
                    <Typography sx={{ fontFamily: 'Arial, sans-serif', fontSize: '14px', color: '#64748B', mb: 0.5 }}>
                      ZIP only (exactly 1 .json, 1 .pt, and 1 image: .jpg, .jpeg, or .png). Max 50MB.
                    </Typography>
                    <Typography sx={{ fontFamily: 'Arial, sans-serif', fontSize: '12px', color: '#94A3B8' }}>Max file size: 50MB</Typography>
                  </>
                )}
              </Box>
            )}

            <input
              id="file-upload-input"
              type="file"
              accept=".zip"
              hidden
              onChange={handleFileInputChange}
            />
          </Box>
        ) : (
          // Step 2: Form
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, p: 1.25 }}>
            {/* Row 1: AI Detection Use Case Name & Use Case Type */}
            <Box sx={{ display: 'flex', gap: 1.25 }}>
              {/* AI Detection Use Case Name */}
              <Box sx={{ flex: 1 }}>
                <Typography
                  sx={{
                    fontFamily: 'Roboto, sans-serif',
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#364153',
                    mb: 1,
                  }}
                >
                  AI Detection Use Case Name{' '}
                  <span style={{ color: '#FB2C36' }}>*</span>
                </Typography>
                <TextField
                  fullWidth
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="PPE Detection"
                  error={!!step2Errors.name}
                  helperText={step2Errors.name ?? undefined}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      height: 41,
                      fontFamily: 'Arial, sans-serif',
                      fontSize: '14px',
                      color: '#808080',
                      borderRadius: '10px',
                      '& fieldset': {
                        borderColor: step2Errors.name ? '#d32f2f' : '#E5E7EB',
                      },
                    },
                  }}
                />
              </Box>

              {/* Use Case Type (read-only from JSON) */}
              <Box sx={{ flex: 1 }}>
                <Typography
                  sx={{
                    fontFamily: 'Roboto, sans-serif',
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#364153',
                    mb: 1,
                  }}
                >
                  Use Case Type <span style={{ color: '#FB2C36' }}>*</span>
                </Typography>
                <TextField
                  fullWidth
                  value={formData.useCaseType}
                  InputProps={{ readOnly: true }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      height: 41,
                      fontFamily: 'Arial, sans-serif',
                      fontSize: '14px',
                      borderRadius: '10px',
                      bgcolor: '#F9FAFB',
                      '& fieldset': { borderColor: '#E5E7EB' },
                      '&.Mui-disabled': { bgcolor: '#F9FAFB' },
                    },
                  }}
                />
              </Box>
            </Box>

            {/* Row 2: Zone Type & Detection Accuracy (read-only from JSON) */}
            <Box sx={{ display: 'flex', gap: 1.25 }}>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '14px', fontWeight: 500, color: '#364153', mb: 1 }}>
                  Zone Type <span style={{ color: '#FB2C36' }}>*</span>
                </Typography>
                <TextField
                  fullWidth
                  value={formData.zoneType}
                  InputProps={{ readOnly: true }}
                  error={!!step2Errors.zoneType}
                  helperText={step2Errors.zoneType ?? undefined}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      height: 41,
                      fontFamily: 'Arial, sans-serif',
                      fontSize: '14px',
                      borderRadius: '10px',
                      bgcolor: '#F9FAFB',
                      '& fieldset': { borderColor: step2Errors.zoneType ? '#d32f2f' : '#E5E7EB' },
                    },
                  }}
                />
              </Box>

              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '14px', fontWeight: 500, color: '#364153', mb: 1 }}>
                  Detection Accuracy <span style={{ color: '#FB2C36' }}>*</span>
                </Typography>
                <TextField
                  type="number"
                  fullWidth
                  value={formData.accuracy ?? ''}
                  InputProps={{ readOnly: true }}
                  error={!!step2Errors.accuracy}
                  helperText={step2Errors.accuracy ?? undefined}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      height: 41,
                      fontFamily: 'Arial, sans-serif',
                      fontSize: '14px',
                      borderRadius: '10px',
                      bgcolor: '#F9FAFB',
                      '& fieldset': { borderColor: step2Errors.accuracy ? '#d32f2f' : '#E5E7EB' },
                    },
                  }}
                />
              </Box>
            </Box>

            {/* Model file (.pt) – read-only, stored for backend */}
            <Box>
              <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '14px', fontWeight: 500, color: '#364153', mb: 1 }}>
                Model file (.pt)
              </Typography>
              <TextField
                fullWidth
                value={formData.modelFile}
                InputProps={{ readOnly: true }}
                helperText="Stored for backend processing; not editable."
                sx={{
                  '& .MuiOutlinedInput-root': {
                    height: 41,
                    fontFamily: 'Arial, sans-serif',
                    fontSize: '14px',
                    borderRadius: '10px',
                    bgcolor: '#F9FAFB',
                    '& fieldset': { borderColor: '#E5E7EB' },
                  },
                }}
              />
            </Box>

            {/* Row 3: Description (editable) & Image (read-only) */}
            <Box sx={{ display: 'flex', gap: 1.25 }}>
              {/* Description */}
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography
                    sx={{
                      fontFamily: 'Roboto, sans-serif',
                      fontSize: '14px',
                      fontWeight: 500,
                      color: '#364153',
                    }}
                  >
                    Description <span style={{ color: '#FB2C36' }}>*</span>
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: 'Roboto, sans-serif',
                      fontSize: '12px',
                      color: formData.description.length > DESCRIPTION_MAX_LENGTH ? '#d32f2f' : '#6B7280',
                    }}
                  >
                    {formData.description.length} / {DESCRIPTION_MAX_LENGTH}
                  </Typography>
                </Box>
                <TextField
                  fullWidth
                  multiline
                  rows={3.5}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value.slice(0, DESCRIPTION_MAX_LENGTH) })}
                  placeholder="Brief overview of the use case..."
                  error={!!step2Errors.description}
                  helperText={step2Errors.description ?? undefined}
                  inputProps={{ maxLength: DESCRIPTION_MAX_LENGTH }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontFamily: 'Arial, sans-serif',
                      fontSize: '14px',
                      color: '#808080',
                      borderRadius: '10px',
                      '& fieldset': {
                        borderColor: step2Errors.description ? '#d32f2f' : '#E5E7EB',
                      },
                    },
                  }}
                />
              </Box>

              {/* Image (read-only from JSON) */}
              <Box sx={{ flex: 0, width: 199 }}>
                <Typography
                  sx={{
                    fontFamily: 'Roboto, sans-serif',
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#364153',
                    mb: 1,
                  }}
                >
                  Image
                </Typography>
                <Box
                  sx={{
                    width: 199,
                    height: 100,
                    borderRadius: '4px',
                    bgcolor: '#E5E7EB',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    border: '1px solid #E5E7EB',
                  }}
                >
                  {(extractedImageObjectUrl ?? (formData.imagePreview && !imagePreviewLoadError ? formData.imagePreview : null)) ? (
                    <img
                      src={extractedImageObjectUrl ?? formData.imagePreview ?? ''}
                      alt="Use case preview from uploaded zip"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                      onError={() => !extractedImageObjectUrl && setImagePreviewLoadError(true)}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: '#E5E7EB',
                        color: '#6B7280',
                        fontFamily: 'Roboto, sans-serif',
                        fontSize: '14px',
                        px: 1,
                        textAlign: 'center',
                      }}
                    >
                      {imagePreviewLoadError ? 'Preview unavailable' : 'No image'}
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
            {uploadModelError && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.5,
                  p: 1.5,
                  borderRadius: 1,
                  bgcolor: '#FEF2F2',
                  border: '1px solid #FECACA',
                }}
              >
                <ErrorOutlineIcon sx={{ color: '#B91C1C', fontSize: 22, flexShrink: 0, mt: 0.25 }} />
                <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: '#991B1B', fontWeight: 500 }}>
                  {uploadModelError}
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* Footer */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          py: 2,
          bgcolor: '#F9FAFB',
          borderTop: '0.8px solid #E5E7EB',
          borderBottomLeftRadius: '10px',
          borderBottomRightRadius: '10px',
        }}
      >
        <Typography
          sx={{
            fontFamily: 'Roboto, sans-serif',
            fontSize: '14px',
            color: '#4A5565',
          }}
        >
          Step {currentStep} of 2
        </Typography>

        <Box sx={{ display: 'flex', gap: 1 }}>
          {currentStep === 1 ? (
            <>
              <Button
                onClick={handleClose}
                sx={{
                  bgcolor: 'white',
                  border: '0.8px solid rgba(0,0,0,0.1)',
                  borderRadius: '8px',
                  color: '#0A0A0A',
                  textTransform: 'none',
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '14px',
                  fontWeight: 500,
                  px: 2,
                  py: 1,
                  boxShadow: 'none',
                  '&:hover': { bgcolor: '#F5F5F5', boxShadow: 'none' },
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={() => handleNext()}
                disabled={!uploadedFile || isUploadingModel}
                sx={{
                  bgcolor: '#2932E5',
                  border: '0.8px solid #2932E5',
                  borderRadius: '8px',
                  color: 'white',
                  textTransform: 'none',
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '14px',
                  fontWeight: 500,
                  px: 2,
                  py: 1,
                  opacity: uploadedFile && !isUploadingModel ? 1 : 0.5,
                  boxShadow: 'none',
                  '&:hover': {
                    bgcolor: '#1f28b8',
                    border: '0.8px solid #1f28b8',
                    boxShadow: 'none',
                  },
                  '&:disabled': {
                    bgcolor: '#2932E5',
                    color: 'white',
                    opacity: 0.5,
                  },
                }}
              >
                {isUploadingModel ? 'Uploading…' : 'Next'}
              </Button>
            </>
          ) : (
            <>
              <Button
                onClick={handleClose}
                sx={{
                  bgcolor: 'white',
                  border: '0.8px solid rgba(0,0,0,0.1)',
                  borderRadius: '8px',
                  color: '#0A0A0A',
                  textTransform: 'none',
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '14px',
                  fontWeight: 500,
                  px: 2,
                  py: 1,
                  boxShadow: 'none',
                  '&:hover': { bgcolor: '#F5F5F5', boxShadow: 'none' },
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleBack}
                sx={{
                  bgcolor: 'white',
                  border: '0.8px solid rgba(0,0,0,0.1)',
                  borderRadius: '8px',
                  color: '#0A0A0A',
                  textTransform: 'none',
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '14px',
                  fontWeight: 500,
                  px: 2,
                  py: 1,
                  boxShadow: 'none',
                  '&:hover': { bgcolor: '#F5F5F5', boxShadow: 'none' },
                }}
              >
                Back
              </Button>
              <Button
                onClick={() => handleSave()}
                disabled={!isStep2Valid || isCreating}
                sx={{
                  bgcolor: '#2932E5',
                  border: '0.8px solid #2932E5',
                  borderRadius: '8px',
                  color: 'white',
                  textTransform: 'none',
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '14px',
                  fontWeight: 500,
                  px: 2,
                  py: 1,
                  boxShadow: 'none',
                  '&:hover': {
                    bgcolor: '#1f28b8',
                    border: '0.8px solid #1f28b8',
                    boxShadow: 'none',
                  },
                  '&:active': {
                    bgcolor: '#fff',
                    color: '#2932E5',
                    borderColor: '#2932E5',
                  },
                  '&:disabled': {
                    bgcolor: '#2932E5',
                    color: 'white',
                    opacity: 0.5,
                  },
                }}
              >
                {isCreating ? 'Creating…' : 'Save'}
              </Button>
            </>
          )}
        </Box>
      </Box>
    </Dialog>
  );
}